"use client";

import { useMemo } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";

import Image, { IImageProps } from "apps/website/components/base/Image/Image";
import Grid from "apps/website/components/layout/Grid/Grid";
import Tag from "apps/website/components/base/Tag/Tag";
import { Theme } from "apps/website/maps/Theme.map";
import Button from "apps/website/components/base/Button/Button";
import TextBody from "apps/website/components/base/Text/TextBody/TextBody";
import TextTitle from "apps/website/components/base/Text/TextTitle/TextTitle";
import { useQueryParams } from "apps/website/hooks/useQueryParams";

import Section from "../../../layout/Section/Section";
import Container from "../../../layout/Container/Container";
import Column from "../../../layout/Column/Column";
import Spacer from "../../../layout/Spacer/Spacer";
import SectionTestimonialCarousel from "../../SectionCarousel/SectionTestimonialCarousel/SectionTestimonialCarousel";
import {
  testimonialCarouselItems,
} from "../../SectionCarousel/SectionTestimonialCarousel/TestimonialCarouselItems";

export type TPrecancellationSubscriptionState = "TRIAL" | "DEFAULT";

export interface ISectionPreCancellationControl {
  theme?: Theme;
  image: IImageProps;
  subscriptionState?: TPrecancellationSubscriptionState;
  catName: string
}

const SectionPreCancellation = ({
  theme,
  image,
  catName,
  subscriptionState = "DEFAULT",
}: ISectionPreCancellationControl) => {

  const tagText = useMemo(() => (
    subscriptionState === "DEFAULT" ?
      "Can we help?" :
      "Still switching?"
  ), [ catName ]);
  const upperText = useMemo(() => (subscriptionState === "DEFAULT" ? `${catName} stopped eating KatKin? Do they only like some recipes? Not ready for your next box?` : `Is ${catName} still adjusting to KatKin? Fresh is different from other cat foods + cats are naturally wary of new routines. That's why it can take time to switch.`), [ catName ]);
  const lowerText = useMemo(() => (
    subscriptionState === "DEFAULT" ?
      <TextBody size={2} align={"center"}>Contact us on <Link id="precancellationPhone" className="underline font-bold" href={"tel:02045384144"}>020 4538 4144</Link> or <Link id="precancellationEmail" className="underline font-bold" href={"mailto:meow@katkin.com"}>drop us an email</Link> so we can provide tailored advice for you and { catName }.</TextBody> :
      <TextBody size={2} align={"center"}>Contact us on <Link id="precancellationPhone" className="underline font-bold" href={"tel:02045384144"}>020 4538 4144</Link> or <Link id="precancellationEmail" className="underline font-bold" href={"mailto:meow@katkin.com"}>meow@katkin.com</Link> so we can provide tailored advice for you and { catName }.</TextBody>
  ), [ catName ]);

  const { chainQueryParamsToUrl } = useQueryParams();

  const pathname = usePathname();
  /**
   * Non-trialist are taken to pre-cancellation-recurring page"
   */
  const isTrialist = pathname === "/pre-cancellation";

  return (
    <>
      <Section component={SectionPreCancellation.name} theme={theme}>
        <Container>
          <Grid>
            <Column align="center" justify="center">
              <div className={"w-24 h-24"}>
                <Image
                  image={image}
                  alt="image"
                />
              </div>
              <Spacer size="sm"></Spacer>
              <Tag theme="dark" className="max-w-[300px]" whitespace="preWrap" size="medium">{ tagText }</Tag>
              <Spacer size="lg"></Spacer>
              <TextBody size={2} align={"center"}>{ upperText }</TextBody>
              <Spacer size="sm"></Spacer>
              { lowerText }
            </Column>
          </Grid>
        </Container>
      </Section>
      { subscriptionState === "DEFAULT" && (
        <Section theme="light-grey">
          <Container>
            <Grid>
              <Column>
                <TextTitle size={3} align="center">{ catName } stopped eating KatKin?</TextTitle>
                <Spacer size="xl" />
                <TextTitle size={1} align="center" whitespace="balance">Our top 3 tips to get { catName } eating again</TextTitle>
              </Column>
            </Grid>
          </Container>
        </Section>
      ) }
      { isTrialist && (
        <>
          <SectionTestimonialCarousel theme="brand" items={testimonialCarouselItems} ></SectionTestimonialCarousel>
          <Section theme="brand-alt">
            <Container>
              <Grid>

                <Column spans={{ lg: 4 }} offset={{ lg: 3 }}>
                  <Button to={ chainQueryParamsToUrl("manage-plan/delay") }>
                Delay box by a week
                  </Button>
                </Column>

                <Column spans={{ lg: 4 }}>
                  <Button to={"/articles/how-to-change-cat-food"} design="underline">
                Or read our transitioning tips
                  </Button>
                </Column>

              </Grid>
            </Container>
          </Section>
        </>
      ) }
    </>
  );
};

export default SectionPreCancellation;
